import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { clientsApi } from '../../api/clients';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Input,
  ListSkeleton,
  Modal,
  PageHeader,
  Pagination,
  Table,
  Td,
  Th,
  Tr,
  useDebounced,
} from '../../components';
import { cn } from '../../lib/cn';
import { formatDate, formatNumber } from '../../lib/format';
import { SentimentBadge } from '../meetings/badges';
import { ClientForm, toClientBody } from './ClientForm';

const PAGE_SIZE = 25;

export function ClientsPage() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const page = Math.max(1, Number(sp.get('page')) || 1);
  const [search, setSearch] = useState(q);
  const debounced = useDebounced(search.trim(), 300);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (debounced !== q) setSp(debounced ? { q: debounced } : {}, { replace: true });
  }, [debounced]); // only react to typing

  const query = useQuery({
    queryKey: ['clients', { q, page }],
    queryFn: () => clientsApi.list({ q: q || undefined, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const setPage = (p: number) => {
    const next = new URLSearchParams(sp);
    if (p > 1) next.set('page', String(p));
    else next.delete('page');
    setSp(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New client
          </Button>
        }
      />
      <div className="mb-3 max-w-sm">
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or domain…" aria-label="Search clients" />
      </div>
      <Card bodyClassName="p-0">
        {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} className="m-3" />}
        {query.isLoading ? (
          <ListSkeleton />
        ) : query.data?.items.length === 0 ? (
          <EmptyState
            title={q ? 'No clients match your search' : 'No clients yet'}
            action={!q && <Button onClick={() => setCreating(true)}>Create the first client</Button>}
          >
            {!q && 'Clients are also created from the desktop app before a call.'}
          </EmptyState>
        ) : query.data ? (
          <div className={cn(query.isPlaceholderData && 'opacity-60')}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Domains</Th>
                  <Th>Industry</Th>
                  <Th>Owner</Th>
                  <Th>Sentiment</Th>
                  <Th className="text-right">Meetings</Th>
                  <Th>Last meeting</Th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      <span className="inline-flex items-center gap-2">
                        <Avatar name={c.name} />
                        <Link to={`/clients/${c.id}`} className="font-medium text-ink hover:text-accent-ink hover:underline">
                          {c.name}
                        </Link>
                      </span>
                    </Td>
                    <Td className="text-ink-2">{c.domains.join(', ') || '—'}</Td>
                    <Td className="text-ink-2">{c.industry ?? '—'}</Td>
                    <Td className="text-ink-2">{c.ownerName ?? '—'}</Td>
                    <Td>
                      <SentimentBadge level={c.sentiment?.level ?? null} />
                    </Td>
                    <Td className="text-right tabular-nums">{formatNumber(c.meetingCount)}</Td>
                    <Td className="whitespace-nowrap text-ink-2">{formatDate(c.lastMeetingAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <div className="border-t border-line">
              <Pagination page={page} pageSize={PAGE_SIZE} total={query.data.total} onPage={setPage} />
            </div>
          </div>
        ) : null}
      </Card>
      <NewClientModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function NewClientModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: clientsApi.create,
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void navigate(`/clients/${client.id}`);
    },
  });
  const close = () => {
    create.reset();
    onClose();
  };
  return (
    <Modal open={open} title="New client" onClose={close}>
      <ClientForm
        initial={{ name: '', industry: '', domains: [], notes: '' }}
        submitLabel="Create client"
        busy={create.isPending}
        error={create.error}
        onSubmit={(v) => create.mutate(toClientBody(v))}
        onCancel={close}
      />
    </Modal>
  );
}
